import { ChangeDetectorRef, Component, OnDestroy, OnInit, Predicate } from '@angular/core';
import { Store } from '@ngrx/store';
import { Department, EmployeeJobCode, IEmployee, ITeam } from '@signal/asp-data-commons';
import { combineLatest, Observable, Subject } from 'rxjs';
import { EmployeeState } from '../../store/employee/employee.reducer';
import { TeamState } from '../../store/team/team.reducer';
import * as fromTeamsSelectors from '../../store/team/team.selectors';
import * as fromEmployeeSelectors from '../../store/employee/employee.selectors';
import * as fromEmployeeActions from '../../store/employee/employee.actions';
import * as fromTeamsActions from '../../store/team/team.actions';
import * as fromSelector from '../../store/sales-availability/sales-availability.selectors';
import { exitConfig, globalTXM } from '../../shared/services/util.service';
import { unsubscribeSubscriptions } from '../../shared/services/util.service';
import { SubscriptionList } from '../../shared/models/asp.types';
import { ShopMaxPerDay, SlotsPerDay, MasterSlot } from '../../shared/models/slot-management.model';
import { SlotState } from '../../store/slot/slot.reducer';
import * as fromSlotsActions from '../../store/slot/slot.actions';
import * as fromSlotsSelector from '../../store/slot/slot.selectors';
import { OpCodeState } from '../../store/opcode/opcode.reducer';
import * as fromOpcodesSelector from '../../store/opcode/opcode.selectors';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { INavigateAway } from '../../shared/guards/NavigationGuard';

@Component({
  selector: 'app-txm-management',
  templateUrl: './txm-management.component.html',
  styleUrls: ['./txm-management.component.scss']
})
export class TxmManagementComponent implements OnInit,INavigateAway, OnDestroy {

  employee: IEmployee;

  team$: Observable<ITeam[]>;
  isTxmTeam: boolean;
  txmTeam: ITeam;
  isTeamCreated: boolean;
  isTxmFlag: boolean = false;
  viewMode="TXM"

  unassignedTxmEmployees$: Observable<string[]>;
  unassignedTxmEmployessCount$: Observable<number>;

  unassignedTxmServiceTechniciansCount$: Observable<number>;
  isLoading$: Observable<boolean>;
  subs: SubscriptionList = {};
  maxTotal$: Observable<ShopMaxPerDay[]>;
  slotDetails$: Observable<SlotsPerDay[]>;
  maxAndMasterSlots$: Observable<{ max: any, masterSlot: MasterSlot[] }>;
  loading = false;
  txmIndicator$: Observable<any>;
  txmActivate: boolean;
  txmTeamMaxTotal$: Observable<any>;
  masterSlotsLoading$: Observable<any>;
  validTxmTeam$: Observable<boolean>;
  isOpcodesLoading$: Observable<boolean>;

   //save clicked 
   isTXMMaxSlotsDaySaveClicked=false
   isSavedClicked=false;

   //cancel clicked
   isCancelClicked=false;
   isTXMMaxSlotsPerTimePeriodCancleClicked=false

  //forms edited
   isTXMPerDayEdited=false;
   isTXMPerTimePeriodEdited=false;
   bothFormsEdited=false;
   slotsUpdated$:Observable<boolean>;
  department = Department.SERVICE;

  constructor(private readonly employeeState: Store<EmployeeState>,
    private readonly teamsState: Store<TeamState>,
    private readonly slotState: Store<SlotState>,
    private readonly opCodeStore: Store<OpCodeState>,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog) {
    this.isTxmFlag = globalTXM;
  }

  ngOnInit() {
    this.isLoading$ = this.teamsState.select(fromTeamsSelectors.selectUpdateEmployeeTeam);
    this.setupStore();
    this.setUpTxmCapacityStore();
    this.isOpcodesLoading$ = this.opCodeStore.select(fromOpcodesSelector.selectOnlineOpCodeLoading);
    this.slotsUpdated$=this.slotState.select(fromSelector.selectRemoteSlotsUpdated);
  }

  ngAfterContentChecked(): void { 
    if (!this.cdr['destroyed']) {
      this.cdr.detectChanges();
    }
  }

  public setupStore() {
    this.dispatchActions();
    this.predicateSelector();
    this.team$ = this.teamsState.select(fromTeamsSelectors.selectTxmTeam);
    this.subs.teamsAndEmployees = combineLatest(this.team$, this.unassignedTxmEmployees$).subscribe(([team, employees]) => {
      if (team?.length <= 0) {
        if (employees?.length > 0) {
          this.isTeamCreated = true;
          this.txmTeam = {
            name: 'TXM',
            teamColor: '#B7FFB7',
            department: Department.SERVICE,
            employeeIds: employees
          }
        }

        else {
          this.isTxmTeam = false;
        }
      }
      else if (team.length > 0) {
        this.isTxmTeam = true;
        this.isTeamCreated = false;
        this.txmTeam = team[0];
        const props = { employeeIds: this.txmTeam.employeeIds };
          this.validTxmTeam$ = this.employeeState.select(fromEmployeeSelectors.selectValidTxmTeamByEmployeesIds, props)
      }
    })

  }

   //checks if navigation is allowed 
 canExit():Observable<boolean> { 
  const result = new Subject<boolean>();
  const dialogRef = this.dialog.open(ConfirmDialogComponent, exitConfig
    );
  this.subs.dialog = dialogRef.afterClosed().subscribe(event => {
    if (event.action === 'confirm') {
      this.onSaveClicked();
      this.subs.slotUpdate = this.slotsUpdated$.subscribe(data=>{
        if(data)
        {
          result.next(true);
          result.complete();
        }
      })
    }
    else if(event.action === 'close'){
      result.next(true);
      result.complete();
    }
  });
  return result.asObservable(); 
};
  private dispatchActions() {
    /* Store actions dispatchers */
    this.teamsState.dispatch(new fromEmployeeActions.LoadEmployees());
     this.teamsState.dispatch(new fromTeamsActions.LoadTeams({ forceReload: true }));
  }

  private predicateSelector() {
    /* Predicates */
    const unassignedTxmServiceAdvisorAndTechnicianPredicate: Predicate<IEmployee> =
      (e: IEmployee) => e.department === Department.SERVICE
        && (e.jobCode === EmployeeJobCode.ADVISOR || e.jobCode === EmployeeJobCode.TECHNICIAN)
        && e.isActive === true
        && !e.teamId && e.isTxm;

    const unassignedTxmServiceTechnicianPredicate: Predicate<IEmployee> =
      (e: IEmployee) => e.department === Department.SERVICE
        && e.jobCode === EmployeeJobCode.TECHNICIAN
        && e.isActive === true
        && !e.teamId && e.isTxm;
    /* Store selectors */
    this.unassignedTxmEmployees$ = this.employeeState.select(fromEmployeeSelectors.selectEmployeeIdsByPredicate, { predicate: unassignedTxmServiceAdvisorAndTechnicianPredicate });
    this.unassignedTxmServiceTechniciansCount$ = this.employeeState.select(fromEmployeeSelectors.selectEmployeesCountByPredicate, { predicate: unassignedTxmServiceTechnicianPredicate });
  }

  setUpTxmCapacityStore(){
    this.slotState.dispatch(new fromSlotsActions.LoadMasterSlots());
    this.slotState.dispatch(new fromSlotsActions.LoadTxmTeamMaxTotal());
      this.slotState.dispatch(new fromSlotsActions.LoadTxmTeamMaxSlots());
    this.maxAndMasterSlots$ = this.slotState.select(fromSlotsSelector.selectMaxAndMasterSlots, {view: 'TXM'});
    this.masterSlotsLoading$ = this.slotState.select(fromSlotsSelector.selectMasterSlotsLoading);
    this.slotState.dispatch(new fromSlotsActions.LoadTxmTeamActivateIndicator());
    this.txmTeamMaxTotal$ = this.slotState.select(fromSlotsSelector.selectTxmTeamMaxTotal);
    this.slotDetails$ = this.slotState.select(fromSlotsSelector.selectTxmTeamMaxSlots);
  
    this.txmIndicator$ = this.slotState.select(fromSlotsSelector.selectTxmActivateIndicators);
    this.subs.txmIndicator = this.txmIndicator$.subscribe(ActivateIndicator => {
      this.txmActivate = ActivateIndicator['indicator'];
    })
  }

  ngOnDestroy() {
    unsubscribeSubscriptions(this.subs);
    this.cdr.detach();
  }
  onSlotsSaveChanges(slotDetails){
    this.slotState.dispatch(new fromSlotsActions.UpdateTxmTeamMaxSlots({ txmTeamMaxSlots: slotDetails,bothFormsEdited:this.bothFormsEdited }));
    this.isSavedClicked = false;
  }
  onTotalSaveChanges(slotDetails){
    this.slotState.dispatch(new fromSlotsActions.UpdateTxmTeamMaxTotal(
      {
        txmTeamMaxTotal : slotDetails
      }));
  }
  updateSwitchValues(val: boolean, property: string) {
    switch (property) {
      case 'txm':
        this.txmActivate = val;
        this.slotState.dispatch(new fromSlotsActions.UpdateTxmTeamActivateIndicator({ indicator : val }))
        break;
        default:
          break;
      }
  }

   // returns if the forms are edited
   formsNotEdited()
   {
     return !this.isTXMPerDayEdited && !this.isTXMPerTimePeriodEdited;
   }
 
    //save button clicked
    onSaveClicked()
    {
      this.isTXMMaxSlotsDaySaveClicked= this.isTXMPerDayEdited?true:false
      this.isSavedClicked=this.isTXMPerTimePeriodEdited?true:false
      this.bothFormsEdited=(this.isTXMPerDayEdited && this.isTXMPerTimePeriodEdited) && this.isTXMPerTimePeriodEdited?true:false
      // detect changes after manually calling onSaveClicked()
      if (!this.cdr['destroyed']) {
        this.cdr.detectChanges();
      }
    }

    //cancel button clicked 
    onCancelClicked()
    {
      this.isCancelClicked= this.isTXMPerDayEdited?true:false
      this.isTXMMaxSlotsPerTimePeriodCancleClicked= this.isTXMPerTimePeriodEdited?true:false
    }

  //reset cancle button clicked value for 1st form
    resetCancleClickValue(event)
    {
      this.isCancelClicked=event
      this.isTXMPerDayEdited=event
    }

   //reset cancle button clicked value for 2nd form
    resetCancleClickedAppTable(event)
    {
      this.isTXMMaxSlotsPerTimePeriodCancleClicked=event
      this.isTXMPerTimePeriodEdited=event
    }

   //reset save button value cliked from 1st from
    resetSaveClickedValue(event)
    {
      this.isTXMMaxSlotsDaySaveClicked=event
      if (!this.cdr['destroyed']) {
        this.cdr.detectChanges();
      }
    }

    //Appointment per day form edited 
    txmPerDayFormEdited(event)
    {
      this.isTXMPerDayEdited=event
    }
  
    //Appointment per time period form edited 
    txmPerTimePeriodEdited(event)
    {
      this.isTXMPerTimePeriodEdited=event
    }
}
